import format from "date-fns/format";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { GetProgressGraphParams, GetProgressGraphResult } from "@/components/home/ProgressDashboard/actions/types";
import storageManager from "@/lib/storageManager";
import { ProgressData } from "@/models/progressGraph";
import { RootState } from "@/reducers/types";

const HOME_PROGRESS_CHART_SEARCH_PARAMS_KEY = "homeProgressChartSearchParams";

export const useProgressGraph = (
  fetchData: (params: GetProgressGraphParams, callback?: (res: GetProgressGraphResult) => void) => void
) => {
  const { masters } = useSelector((state: RootState) => state.construction);

  const [fetching, setFetching] = useState(true);

  // 画面表示データ
  const [updatedAt, setUpdateAt] = useState<Date>();
  const [progressData, setProgressData] = useState<ProgressData[]>();

  // 検索条件
  const [categoryId, setCategoryId] = useState<number>();
  const [primaryChargeId, setPrimaryChargeId] = useState<number>();
  const [areaId, setAreaId] = useState<number>();
  const [deviceId, setDeviceId] = useState<number>();
  const [term, setTerm] = useState<"all" | "two_weeks">("all");

  const generateTerm = (term: "all" | "two_weeks"): [string, string] => {
    if (term === "two_weeks") {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 14);

      return [
        format(new Date(start.getFullYear(), start.getMonth(), start.getDate()), "yyyy-MM-dd"),
        format(new Date(end.getFullYear(), end.getMonth(), end.getDate()), "yyyy-MM-dd"),
      ];
    }

    return [null, null];
  };

  // 初期表示で検索する
  // ローカルストレージに保存した条件がある場合は復元する
  useEffect(() => {
    const storedParams = storageManager.getConstructionItem(HOME_PROGRESS_CHART_SEARCH_PARAMS_KEY);
    const params = storedParams ? JSON.parse(storedParams) : {};
    params?.category_id && setCategoryId(params.category_id);
    params?.primary_charge_id && setPrimaryChargeId(params.primary_charge_id);
    params?.area_id && setAreaId(params.area_id);
    params?.device_id && setDeviceId(params.device_id);
    const term = params?.date_from || params?.date_to ? "two_weeks" : "all";
    setTerm(term);
    const [start, end] = generateTerm(term);

    search({ ...params, date_from: start, date_to: end });
  }, []);

  // 検索条件の変更
  const changeTerm = (term: "all" | "two_weeks") => {
    setTerm(term);
    const [start, end] = generateTerm(term);
    search({ date_from: start, date_to: end });
  };

  const changeCategory = (id: number) => {
    setCategoryId(id);
    search({ category_id: id });
  };

  const changePrimaryCharge = (id: number) => {
    setPrimaryChargeId(id);
    search({ primary_charge_id: id });
  };
  const changeArea = (id: number) => {
    setAreaId(id);
    search({ area_id: id });
  };
  const changeDevice = (id: number) => {
    setDeviceId(id);
    search({ device_id: id });
  };

  // 検索
  const search = (param: Partial<GetProgressGraphParams>) => {
    setFetching(true);
    const [start, end] = generateTerm(term);
    const params = {
      category_id: categoryId,
      primary_charge_id: primaryChargeId,
      area_id: areaId,
      device_id: deviceId,
      date_from: start,
      date_to: end,
      ...param,
    };
    storageManager.setConstructionItem(HOME_PROGRESS_CHART_SEARCH_PARAMS_KEY, JSON.stringify(params));
    fetchData(params, (res) => {
      setUpdateAt(res.graph_info.update_date ? new Date(res.graph_info.update_date) : null);
      setProgressData(res.progress_data);
      setFetching(false);
    });
  };

  return {
    fetching,
    term,
    changeTerm,
    categoryId,
    primaryChargeId,
    areaId,
    deviceId,
    changeCategory,
    changePrimaryCharge,
    changeArea,
    changeDevice,
    categories: masters?.categories ?? [],
    primaryCharges: masters?.primary_charges ?? [],
    areas: masters?.areas ?? [],
    devices: masters?.devices ?? [],

    updatedAt,
    progressData,
  };
};
